import React, { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { RefreshIcon, ShareIcon } from "@/components/icons";
import { PageLayout, PageHeader } from "@/components/PageLayout";
import { cn } from "@/utils/cn";
import { MonthSelector } from "./components/MonthSelector";
import { ReportSummaryModal } from "./components/ReportSummaryModal";
import { TopPostsSection } from "./components/TopPostsSection";
import { ReportCanvas, dropAtPoint, type DropAt } from "./canvas/ReportCanvas";
import { ReportPanelById } from "./canvas/ReportPanelById";
import { ReportRail } from "./canvas/ReportRail";
import { rectsOf, type Edge } from "./canvas/canvasLayout";
import { isPanelId, panelTitle, type PanelId } from "./canvas/panels";
import { useReportingView } from "./hooks/useReportingView";

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** A press on a rail item moves this far before it counts as a drag. */
const DRAG_THRESHOLD = 6;

const COUNT = ["none", "one", "two", "three", "four"];
const count = (n: number) => COUNT[n] ?? String(n);
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** "A", "A and B", "A, B and C". */
const listOf = (items: string[]) =>
  items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** Reporting as one state model drawn per width. Desktop: a rail of folded
 *  panels beside a canvas of one to four opened ones. Tablet: the same, two
 *  tiles at most, a larger layout's first two shown and the rest named.
 *  Phone: one column of every panel, the open ones expanded in place. The
 *  layout lives in the URL; placing, dragging, a refusal and a flash are
 *  this page's own visual state. */
export const ReportingView: React.FC = () => {
  const view = useReportingView();
  const wide = view.tier !== "phone";
  const [placing, setPlacing] = useState<PanelId | null>(null);
  const [dragging, setDragging] = useState(false);
  const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null);
  const [dropAt, setDropAt] = useState<DropAt | null>(null);
  const [refused, setRefused] = useState<PanelId | null>(null);
  const [flash, setFlash] = useState<PanelId | null>(null);
  // The tile a rail click fills in place of its panel; it follows the panel it was given, so clicks keep replacing it.
  const [selected, setSelected] = useState<PanelId | null>(null);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const press = useRef<{ panel: PanelId; x: number; y: number } | null>(null);
  // A drag ends in a click on whatever sits under the release; this swallows it.
  const justDragged = useRef(false);

  const cancelPlacing = useCallback(() => {
    setPlacing(null);
    setDragging(false);
    setGhost(null);
    setDropAt(null);
  }, []);

  useEffect(() => {
    if (!placing && !selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      cancelPlacing();
      setSelected(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [placing, selected, cancelPlacing]);

  useEffect(() => {
    if (!refused) return;
    const timer = window.setTimeout(() => setRefused(null), 4000);
    return () => window.clearTimeout(timer);
  }, [refused]);

  useEffect(() => {
    if (!flash) return;
    const timer = window.setTimeout(() => setFlash(null), 900);
    return () => window.clearTimeout(timer);
  }, [flash]);

  const { openPanels, layout, canAdd } = view;
  // The drag listeners live on window for the whole press, so they read the page through a ref.
  const latest = useRef({ openPanels, layout, canAdd, open: view.open, place: view.place });
  latest.current = { openPanels, layout, canAdd, open: view.open, place: view.place };
  const dropRef = useRef<DropAt | null>(null);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const start = press.current;
      if (!start) return;
      if (!justDragged.current) {
        if (Math.hypot(e.clientX - start.x, e.clientY - start.y) < DRAG_THRESHOLD) return;
        const { openPanels: open, canAdd: room } = latest.current;
        // An open panel or a full canvas has nowhere to go: let the press end as a click, which says why.
        if (open.includes(start.panel) || !room) {
          press.current = null;
          return;
        }
        justDragged.current = true;
        document.body.style.userSelect = "none";
        setPlacing(start.panel);
        setDragging(true);
        setRefused(null);
      }
      setGhost({ x: e.clientX, y: e.clientY });
      // A held mouse button sends no hover to the drop targets, so the drag reads what is under it.
      dropRef.current = dropAtPoint(e.clientX, e.clientY, isPanelId);
      setDropAt(dropRef.current);
    };
    const onUp = (e: PointerEvent) => {
      const start = press.current;
      if (!start) return;
      press.current = null;
      if (!justDragged.current) return;
      document.body.style.userSelect = "";
      // A release over a target drops the panel there; anywhere else, or a cancelled pointer, drops nothing.
      const drop = e.type === "pointerup" ? dropRef.current : null;
      if (drop === "empty") latest.current.open(start.panel);
      else if (drop) latest.current.place(start.panel, drop.target, drop.edge);
      dropRef.current = null;
      setPlacing(null);
      setDragging(false);
      setGhost(null);
      setDropAt(null);
      window.setTimeout(() => {
        justDragged.current = false;
      }, 0);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, []);

  /** A click in the rail opens the panel at once: in the picked tile, else in the next free spot, else, on a
   *  full canvas, in the tile the person picks next. */
  const pick = (id: PanelId) => {
    if (justDragged.current) return;
    setRefused(null);
    if (!wide) {
      // A phone expands the panel in place; an open one folds from its own header.
      if (openPanels.includes(id)) return;
      if (canAdd) view.add(id);
      else setRefused(id);
      return;
    }
    if (view.hiddenPanels.includes(id)) {
      cancelPlacing();
      setRefused(id);
      return;
    }
    if (openPanels.includes(id)) {
      cancelPlacing();
      setFlash(id);
      return;
    }
    if (layout == null) {
      view.open(id);
      return;
    }
    if (target) {
      view.replace(target, id);
      setSelected(id);
      return;
    }
    if (canAdd) {
      view.add(id);
      return;
    }
    setPlacing((p) => (p === id ? null : id));
  };

  const replaceWith = (tileId: PanelId) => {
    if (!placing) return;
    view.replace(tileId, placing);
    cancelPlacing();
  };

  // Mouse only: on touch a press scrolls the rail.
  const pressStart = (id: PanelId, e: React.PointerEvent) => {
    if (!wide || e.pointerType !== "mouse" || e.button !== 0) return;
    press.current = { panel: id, x: e.clientX, y: e.clientY };
  };

  const placeAt = (target: PanelId, edge: Edge) => {
    if (!placing) return;
    view.place(placing, target, edge);
    cancelPlacing();
  };

  const renderPanel = (id: PanelId) => (
    <ReportPanelById
      id={id}
      clientId={view.clientId}
      year={view.year}
      month={view.month}
      social={view.social}
      postDays={view.postDays}
      onRefreshPlatform={view.refreshPlatform}
      refreshingPlatform={view.refreshingPlatform}
    />
  );

  /** Why `id` cannot open, in words that say what to do about it. */
  const reasonFor = (id: PanelId): string => {
    const title = panelTitle(id);
    const total = openPanels.length;
    if (!wide) return `${sentence(count(total))} panels are open: ${listOf(openPanels.map(panelTitle))}. Fold one to open ${title}.`;
    if (view.hiddenPanels.includes(id)) {
      return `${title} is in this layout, past the ${count(view.cap)} panels this screen shows. It shows on a wider screen.`;
    }
    return `The canvas holds ${count(view.cap)} panels. Close one to open ${title}.`;
  };
  const refusal = refused ? { panel: refused, reason: reasonFor(refused) } : null;
  const shownTiles = rectsOf(view.shown).flatMap((r) => (isPanelId(r.panel) ? [{ ...r, panel: r.panel }] : []));
  const shownPanels = shownTiles.map((t) => t.panel);
  // A picked tile that has gone (closed, or Back changed the layout) is let go.
  const target = selected && shownPanels.includes(selected) ? selected : null;
  // A rail click with no free spot waits for a tile to replace, where a drag waits for an edge.
  const replacing = placing != null && !dragging && !canAdd;
  const hidden = view.hiddenPanels;

  if (!view.clientId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Please select a client to view reports</p>
      </div>
    );
  }

  // Top posts are post cards on the page (DD-008), not a panel: a section
  // after the rail and canvas, or at the end of the phone's column.
  const topPosts =
    view.social.status === "ready" ? <TopPostsSection topPosts={view.social.report.topPosts} /> : null;

  const headerActions = (
    <PageHeader.Actions>
      {wide && placing && (
        <span role="status" className="flex items-center gap-2 text-[13px] text-gray-600">
          <span>
            {replacing ? "The canvas is full: pick a panel to replace with " : "Place "}
            <span className="font-medium text-gray-900">{panelTitle(placing)}</span>
            {replacing ? "" : ": pick an edge"}
          </span>
          <button type="button" onClick={cancelPlacing} className="text-blue-600 hover:text-blue-700 font-medium">
            Cancel
          </button>
          <span className="text-gray-400">Esc</span>
        </span>
      )}
      {wide && !placing && target && (
        <span role="status" className="flex items-center gap-2 text-[13px] text-gray-600">
          <span>
            Panels you click open in place of <span className="font-medium text-gray-900">{panelTitle(target)}</span>
          </span>
          <button type="button" onClick={() => setSelected(null)} className="text-blue-600 hover:text-blue-700 font-medium">
            Done
          </button>
          <span className="text-gray-400">Esc</span>
        </span>
      )}
      {view.hasReport && (
        <button
          onClick={() => setShowSummaryModal(true)}
          className="inline-flex items-center justify-center max-md:min-h-[44px] max-md:min-w-[44px] px-2.5 py-1.5 text-sm font-medium rounded-md text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
          title="Report summary"
        >
          <ShareIcon size={14} className="mr-1 text-gray-400" />
          <span className="text-xs">Summary</span>
        </button>
      )}
      <button
        onClick={() => setShowRefreshConfirm(true)}
        disabled={view.isRefreshing || view.social.status === "loading" || !!view.refreshingPlatform}
        className="inline-flex items-center justify-center max-md:min-h-[44px] max-md:min-w-[44px] px-2.5 py-1.5 text-sm font-medium rounded-md text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        title="Fetch fresh data"
      >
        <RefreshIcon size={14} className={`mr-1 text-gray-400 ${view.isRefreshing ? "animate-spin" : ""}`} />
        <span className="text-xs">{view.isRefreshing ? "..." : "Refresh"}</span>
      </button>
      {/* The month selects are the app's own; on a phone they grow to a 44px touch target. */}
      <span className="contents max-md:[&_select]:min-h-[44px] max-md:[&_button]:min-h-[44px]">
        <MonthSelector selectedYear={view.year} selectedMonth={view.month} onLoadReport={view.setPeriod} />
      </span>
    </PageHeader.Actions>
  );

  return (
    // On a phone the page itself scrolls, header included, so the month and actions give their height back to the panels.
    <PageLayout className={wide ? undefined : "overflow-y-auto overscroll-contain"}>
      <PageHeader>
        <PageHeader.Row>
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">{view.periodLabel}</h1>
            {view.comparesPrevious && (
              <span className="shrink-0 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">vs prev</span>
            )}
            {view.updatedAt && <span className="text-xs text-gray-400 shrink-0">{formatRelativeTime(view.updatedAt)}</span>}
          </div>
          {headerActions}
        </PageHeader.Row>
      </PageHeader>

      {wide ? (
        // The rail and canvas fill the window under the header; the page scrolls past them to Top posts.
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="h-full flex gap-4 px-4 lg:px-6 pt-1 pb-4 lg:pb-6">
            <ReportRail
              // 4px of padding so the open ring is never clipped by the scroll box.
              className={cn(
                "shrink-0 overflow-y-auto overflow-x-hidden overscroll-contain p-1 pb-3 -m-1",
                view.tier === "tablet" ? "w-[280px]" : "w-[308px]"
              )}
              renderPanel={renderPanel}
              active={shownPanels}
              hidden={hidden}
              placing={placing}
              refused={refusal}
              onPick={pick}
              onPressStart={pressStart}
            />
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="relative flex-1 min-h-0">
                <ReportCanvas
                  tiles={shownTiles}
                  renderPanel={renderPanel}
                  titleOf={panelTitle}
                  onClose={view.close}
                  placing={placing}
                  dragging={dragging}
                  dropAt={dropAt}
                  edgesFor={view.edgesFor}
                  onPlace={placeAt}
                  flash={flash}
                  selected={target}
                  onSelect={setSelected}
                  replacing={replacing}
                  onReplace={replaceWith}
                  cap={view.cap}
                />
              </div>
              {hidden.length > 0 && (
                // Never dropped from the URL: a wider screen draws them again.
                <p role="note" className="shrink-0 px-1 text-[13px] text-gray-500">
                  {sentence(count(hidden.length))} more {hidden.length === 1 ? "panel" : "panels"} in this layout,{" "}
                  {listOf(hidden.map(panelTitle))}, {hidden.length === 1 ? "shows" : "show"} on a wider screen.
                </p>
              )}
            </div>
          </div>
          {topPosts && <div className="px-4 lg:px-6 pb-4 lg:pb-6">{topPosts}</div>}
        </div>
      ) : (
        // A phone: one column under the header, all of it one scroll, and no panel scrolls inside it.
        <div className="shrink-0 px-4 pb-6">
          <ReportRail
            renderPanel={renderPanel}
            active={[]}
            expanded={openPanels}
            onFold={view.close}
            placing={null}
            refused={refusal}
            onPick={pick}
          />
          {topPosts && <div className="mt-6">{topPosts}</div>}
        </div>
      )}

      {dragging && ghost && placing && (
        <div
          className="fixed z-50 pointer-events-none rounded-full bg-gray-900 px-3 py-1.5 text-[13px] font-medium text-white"
          style={{ left: ghost.x + 14, top: ghost.y + 14 }}
        >
          {panelTitle(placing)}
        </div>
      )}

      <ConfirmDialog
        isOpen={showRefreshConfirm}
        onClose={() => setShowRefreshConfirm(false)}
        onConfirm={() => {
          setShowRefreshConfirm(false);
          view.refreshReport();
        }}
        title="Refresh Report Data"
        message="This will fetch fresh data from the Facebook and Instagram APIs. It may take a few moments."
        confirmText="Refresh"
        variant="info"
        isLoading={view.isRefreshing}
      />

      <ReportSummaryModal isOpen={showSummaryModal} onClose={() => setShowSummaryModal(false)} clientId={view.clientId} />
    </PageLayout>
  );
};
